import { iconAlertaSucesso, iconAlertaSucessoPreenchido } from '@bsds-lib-component/icons/icons';
import { BsdsIcon } from '@bsds-lib-component/react';
import React, { useId, useMemo } from 'react';

import type { StepperXProps } from '../../types/componentType';

const StepperX: React.FC<StepperXProps> = ({ totalSteps, currentStep }) => {
  const stepperId = useId();

  const steps = useMemo(
    () => Array.from({ length: totalSteps }, (_, index) => index + 1),
    [totalSteps]
  );

  type StepStatus = 'complete' | 'current' | 'pending';

  const getStepStatus = (step: number): StepStatus => {
    if (step < currentStep) return 'complete';
    if (step === currentStep) return 'current';
    return 'pending';
  };

  const getStepAriaLabel = (step: number) => {
    const status = getStepStatus(step);
    const statusText =
      status === 'complete' ? 'concluído' : status === 'current' ? 'atual' : 'pendente';
    return `Passo ${step} de ${totalSteps} (${statusText})`;
  };

  return (
    <nav
      className="mb-4"
      role="navigation"
      aria-label="Progresso por etapas"
      id={stepperId}
    >
      <ol className="flex items-center justify-center" role="list">
        {steps.map((step) => {
          const stepId = `${stepperId}-step-${step}`;
          const status = getStepStatus(step);
          const isComplete = status === 'complete';
          const isCurrent = status === 'current';
          const isActive = isComplete || isCurrent;

          return (
            <li key={step} className="flex items-center" role="listitem">
              <div
                className="relative flex items-center justify-center w-10 h-10"
                role="img"
                aria-label={getStepAriaLabel(step)}
                id={stepId}
              >
                {isCurrent && (
                  <span
                    className="absolute w-9 h-9 rounded-full border-2 border-green-700/80 pointer-events-none"
                    aria-hidden="true"
                  />
                )}
                <div
                  className={`relative w-7 h-7 rounded-full flex items-center justify-center border-[5px] ${
                    isActive
                      ? 'bg-green-base/10 border-green-700/80'
                      : 'bg-gray-50 border-gray-100'
                  }`}
                >
                  <BsdsIcon
                    icon={isActive ? iconAlertaSucesso : iconAlertaSucessoPreenchido}
                    className={`w-4.5 h-4.5 ${
                      isActive ? 'text-green-700/80' : 'text-gray-600'
                    }`}
                    aria-hidden="true"
                  />
                </div>
              </div>
              {step < totalSteps && (
                <div
                  className={`w-44 h-1.5 -mx-0.5 ${isComplete ? 'bg-green-700/80' : 'bg-gray-100'}`}
                  role="presentation"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default StepperX;
